import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { CheckCircle, Shield, AlertTriangle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function MySubscription() {
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/subscriptions/my`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setSubscriptions(data);
        }
      } catch (error) {
        toast.error('Failed to load subscriptions');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptions();
  }, []);

  if (loading) {
    return (
      <DashboardLayout title="My Subscription">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Subscription">
      <div className="space-y-6" data-testid="subscription-page">
        {subscriptions.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="no-subscription-message">
            <AlertTriangle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-mono font-bold mb-2">No Active Subscription</h3>
            <p className="text-muted-foreground">Contact your administrator to set up a subscription.</p>
          </div>
        ) : (
          subscriptions.map((subscription) => {
            // Get all products and active products from the subscription
            const allProducts = subscription.products || [];
            const activeProducts = subscription.active_products || [];
            
            return (
              <div key={subscription.subscription_id} className="bg-card border border-primary rounded-lg overflow-hidden" data-testid={`subscription-card-${subscription.subscription_id}`}>
                {/* Header */}
                <div className="bg-primary/10 border-b border-primary/20 p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <Shield className="h-8 w-8 text-primary" />
                        <h2 className="text-2xl font-mono font-bold text-primary" data-testid="subscription-tier">
                          Cyber {subscription.tier.toUpperCase()}
                        </h2>
                      </div>
                      <p className="text-sm text-muted-foreground font-mono">
                        Active since {new Date(subscription.start_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="px-4 py-2 rounded-full bg-primary/20 border border-primary/20">
                      <span className="text-primary font-mono font-bold text-sm" data-testid="subscription-status">
                        {subscription.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  {subscription.monthly_cost && (
                    <p className="mt-4 text-3xl font-mono font-bold" data-testid="subscription-cost">
                      £{subscription.monthly_cost}/month
                    </p>
                  )}
                </div>
                
                {/* Active Products */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-mono font-bold">Your Active Products</h3>
                    <span className="text-sm text-muted-foreground font-mono">
                      {activeProducts.length} of {allProducts.length} products active
                    </span>
                  </div>
                  
                  {activeProducts.length === 0 ? (
                    <div className="text-center py-8 bg-muted/10 rounded-lg border border-border">
                      <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground font-mono">No products are currently active</p>
                      <p className="text-sm text-muted-foreground mt-1">Contact your administrator to activate products</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                      {activeProducts.map((product, index) => (
                        <div 
                          key={index} 
                          className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-md"
                          data-testid={`active-product-${index}`}
                        >
                          <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                          <span className="text-sm font-mono">{product}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Inactive Products (if any) */}
                  {allProducts.filter(p => !activeProducts.includes(p)).length > 0 && (
                    <div className="mt-6 pt-6 border-t border-border">
                      <h4 className="text-sm font-mono font-bold text-muted-foreground mb-3">
                        Available in Your Plan (Not Active)
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {allProducts.filter(p => !activeProducts.includes(p)).map((product, index) => (
                          <div 
                            key={index} 
                            className="flex items-center gap-3 p-2 bg-muted/5 border border-border rounded-md opacity-60"
                          >
                            <XCircle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="text-xs font-mono text-muted-foreground">{product}</span>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-3">
                        Contact your administrator to activate these products.
                      </p>
                    </div>
                  )}
                  
                  {/* Renewal Info */}
                  {subscription.renewal_date && (
                    <div className="mt-6 pt-6 border-t border-border">
                      <p className="text-sm font-mono text-muted-foreground">
                        Next renewal: <span className="text-foreground font-bold" data-testid="renewal-date">
                          {new Date(subscription.renewal_date).toLocaleDateString()}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </DashboardLayout>
  );
}

export default MySubscription;
